import { useEffect, useMemo, useRef, useState } from 'react'
import { Button, Chip, Icon, Modal, PageHead, StateBlock } from '@w7/ui'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { accountSecurityUrl, getAuthConfig } from '@/auth/config'
import {
  isDemoSession,
  removeMfaCredential,
  beginChangePassword,
  peekLastKcActionResult,
} from '@/auth/oidc'
import {
  forgetCredential,
  clearLocalMethodActive,
  isDeletableCredentialId,
  markMethodEnrolled,
  canonicalMfaMethodId,
  methodIdsForCredentialType,
  rivalPrimaryMfaId,
  activePrimaryMfaId,
  isPrimaryMfaMethod,
  type MfaCredentialInfo,
} from '@/auth/mfaStatus'
import { webAuthnNeedsLocalhost } from '@/auth/webauthnHost'
import { useMfaStatus } from '@/api/hooks/shared/useMfaStatus'
import { keys } from '@/api/hooks/shared/keys'
import { toastCopy, useAppToast } from '@/ui/toaster'
import { MFA_HUB_METHODS, getMfaMethod, type MfaMethod } from './mfaMethods'
import './security.css'

function formatWhen(ms?: number): string {
  if (!ms) return 'Recently'
  try {
    return new Date(ms).toLocaleString(undefined, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    })
  } catch {
    return 'Recently'
  }
}

/** Credentials that belong on this method card only (never password). */
function credsForMethod(methodId: string, all: MfaCredentialInfo[]): MfaCredentialInfo[] {
  return all.filter((c) => {
    const t = c.type.toLowerCase()
    if (t === 'password' || t === 'password-history') return false
    return methodIdsForCredentialType(t).includes(methodId)
  })
}

function isWebAuthnShortcut(methodId: string): boolean {
  return methodId === 'fingerprint' || methodId === 'security-key'
}

/** Only these map to a real Keycloak credential you can delete. */
function isRemovableMethod(methodId: string): boolean {
  return methodId === 'totp' || methodId === 'passkey' || methodId === 'recovery'
}

function shortFactorLabel(id: string): string {
  if (id === 'totp') return 'Authenticator'
  if (id === 'passkey') return 'Passkey'
  if (id === 'recovery') return 'Recovery codes'
  return getMfaMethod(id)?.title ?? id
}

type RemoveIntent =
  | { kind: 'all' }
  | { kind: 'method'; methodId: 'totp' | 'passkey' | 'recovery' }

function removeCopy(intent: RemoveIntent): { title: string; subtitle: string } {
  if (intent.kind === 'all') {
    return {
      title: 'Turn off two-factor authentication',
      subtitle: 'Delete each device below — Keycloak confirms, then you return here',
    }
  }
  if (intent.methodId === 'totp') {
    return {
      title: 'Remove authenticator app',
      subtitle: 'Pick the device below — removed directly (same as CM Console 2FA)',
    }
  }
  if (intent.methodId === 'recovery') {
    return {
      title: 'Remove recovery codes',
      subtitle: 'Pick the recovery set below to delete',
    }
  }
  return {
    title: 'Remove passkey',
    subtitle: 'Pick the device below (Touch ID, fingerprint, Face ID, or USB key)',
  }
}

function credTypeLabel(type: string): string {
  const t = type.toLowerCase()
  if (t === 'otp' || t === 'totp' || t === 'hotp') return 'Authenticator app'
  if (t.includes('recovery')) return 'Recovery codes'
  if (t.includes('webauthn')) return 'Passkey / device'
  return type
}

/** CM-style device line: named device first, then type · added date · id tail. */
function deviceDisplayName(cred: MfaCredentialInfo): string {
  const label = cred.userLabel?.trim()
  if (label) return label
  return credTypeLabel(cred.type)
}

function deviceDisplayMeta(cred: MfaCredentialInfo): string {
  const kind = credTypeLabel(cred.type)
  const when = formatWhen(cred.createdDate)
  const tail = cred.id && !cred.id.startsWith('local-') ? ` · …${cred.id.slice(-6)}` : ''
  return `${kind} · added ${when}${tail}`
}

function MethodTile({
  method,
  active,
  coveredByPasskey,
  blockedByRival,
  demo,
  onSetup,
  onRemove,
}: {
  method: MfaMethod
  active: boolean
  coveredByPasskey: boolean
  /** Other primary factor title when this card cannot be enrolled yet. */
  blockedByRival?: string
  demo: boolean
  onSetup: () => void
  onRemove?: () => void
}) {
  const state = active
    ? 'active'
    : blockedByRival
      ? 'muted'
      : coveredByPasskey
        ? 'covered'
        : method.status === 'ready'
          ? 'ready'
          : 'muted'
  const line = blockedByRival
    ? `Available after you remove ${blockedByRival} — only one sign-in method at a time`
    : coveredByPasskey && !active
      ? 'Covered by your passkey — remove that to turn this off'
      : method.blurb
  const setupDisabled = demo || Boolean(blockedByRival)

  return (
    <article className="w7-mfa-tile" data-state={state} data-testid={`mfa-tile-${method.id}`}>
      <div className="w7-mfa-tile-media" aria-hidden>
        <img src={method.image} alt="" loading="lazy" decoding="async" />
      </div>
      <div className="w7-mfa-tile-scrim" aria-hidden />

      <div className="w7-mfa-tile-overlay">
        <div className="w7-mfa-tile-toprow">
          {active ? (
            <span className="w7-mfa-active-pill" data-testid={`mfa-active-${method.id}`}>
              Activated
            </span>
          ) : blockedByRival ? (
            <Chip size="sm" tone="warn">
              Off while {blockedByRival} is on
            </Chip>
          ) : coveredByPasskey ? (
            <Chip size="sm" tone="info">
              Via passkey
            </Chip>
          ) : (
            <Chip
              size="sm"
              tone={method.status === 'ready' ? 'info' : method.status === 'soon' ? 'warn' : 'muted'}
            >
              {method.statusLabel}
            </Chip>
          )}
        </div>

        <div className="w7-mfa-tile-copy">
          <h3 className="w7-mfa-tile-title">{method.title}</h3>
          <p className="w7-mfa-tile-body">{line}</p>
        </div>

        {method.kcAction && method.actionLabel ? (
          <div className="w7-mfa-tile-actions">
            <Button
              variant={active ? 'secondary' : 'primary'}
              size="sm"
              icon="shield"
              onClick={onSetup}
              disabled={setupDisabled}
              data-testid={`mfa-setup-${method.id}`}
              title={
                blockedByRival
                  ? `Remove ${blockedByRival} first to register this method`
                  : undefined
              }
            >
              {active
                ? method.id === 'totp'
                  ? 'Replace app'
                  : 'Add another'
                : coveredByPasskey
                  ? 'Register another'
                  : method.actionLabel}
            </Button>
            {onRemove ? (
              <Button variant="secondary" size="sm" icon="close" disabled={demo} onClick={onRemove}>
                Remove
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>
    </article>
  )
}

/**
 * Hub for optional 2FA. Enroll in-console; remove/turn-off via guided Keycloak steps.
 */
export function SecuritySettingsPage() {
  const toast = useAppToast()
  const navigate = useNavigate()
  const [search, setSearch] = useSearchParams()
  const demo = isDemoSession()
  const enrolled = search.get('enrolled')
  const removed = search.get('removed')
  const passkeyConflict = search.get('passkey_conflict') === '1'
  const qc = useQueryClient()
  const statusQuery = useMfaStatus()
  const status = statusQuery.data
  const [removeIntent, setRemoveIntent] = useState<RemoveIntent | null>(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [pwdBusy, setPwdBusy] = useState(false)
  const toastedEnroll = useRef<string | null>(null)
  const toastedRemove = useRef<string | null>(null)
  const toastedPasskeyConflict = useRef(false)
  const toastedPasskeyHost = useRef(false)
  const passkeyHostBroken = webAuthnNeedsLocalhost(
    typeof window !== 'undefined' ? window.location.hostname : 'localhost',
    getAuthConfig().keycloakUrl || undefined,
  )

  useEffect(() => {
    if (!passkeyHostBroken || toastedPasskeyHost.current) return
    toastedPasskeyHost.current = true
    toast.show(toastCopy.mfaPasskeyHost)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [passkeyHostBroken])

  // Register failed in Keycloak (“already exists”) but Account list may still be empty.
  useEffect(() => {
    if (!passkeyConflict || toastedPasskeyConflict.current) return
    toastedPasskeyConflict.current = true
    search.delete('passkey_conflict')
    setSearch(search, { replace: true })
    void (async () => {
      await qc.invalidateQueries({ queryKey: keys.mfaStatus })
      const fresh = await statusQuery.refetch()
      const ids = fresh?.data?.activeMethodIds ?? []
      if (ids.includes('passkey')) return
      // Optimistic Active so Remove / Turn off appear; real UUID comes after Refresh
      // once Account/Admin lists the device (or Keycloak Account manage).
      markMethodEnrolled('passkey')
      void qc.invalidateQueries({ queryKey: keys.mfaStatus })
      const account = accountSecurityUrl()
      toast.show({
        tone: 'warn',
        message: 'Passkey may already be on this account',
        detail: account
          ? `This console could not list the device id yet. Refresh status, or manage it in Keycloak Account (${account}). Re-register only with a unique device name.`
          : 'Refresh status, or manage the passkey in Keycloak Account. Re-register only with a unique device name.',
      })
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [passkeyConflict, qc])

  useEffect(() => {
    if (!enrolled) return
    if (toastedEnroll.current === enrolled) return
    toastedEnroll.current = enrolled
    const methodKey = enrolled
    const alias = getMfaMethod(methodKey)
    const canonicalId = canonicalMfaMethodId(methodKey)
    const canonical = getMfaMethod(canonicalId)
    const title = (canonical ?? alias)?.title ?? 'Method'
    search.delete('enrolled')
    setSearch(search, { replace: true })
    void (async () => {
      // IdP is source of truth — do not toast “active” from ?enrolled= alone.
      // Passkey often returns to the app with success UX while Keycloak stored nothing
      // (WebAuthn cancel/error, duplicate label, or null kc_action_status).
      // Brief retries: Account REST can lag a beat behind a real register.
      let ids: string[] = []
      for (let attempt = 0; attempt < 3; attempt++) {
        if (attempt > 0) await new Promise((r) => setTimeout(r, 500))
        await qc.invalidateQueries({ queryKey: keys.mfaStatus })
        const fresh = await statusQuery.refetch()
        ids = fresh?.data?.activeMethodIds ?? []
        if (ids.includes(canonicalId)) break
      }
      if (ids.includes(canonicalId)) {
        markMethodEnrolled(methodKey)
        const detail =
          canonical && alias && canonical.id !== alias.id
            ? `You enrolled via ${alias.title}. Keycloak stores that as one passkey — see the ${canonical.title} card.`
            : undefined
        toast.mfaEnrolled(title, detail)
        return
      }
      clearLocalMethodActive(canonicalId)
      void qc.invalidateQueries({ queryKey: keys.mfaStatus })
      const aia = peekLastKcActionResult()
      const aiaBit =
        aia?.action != null
          ? ` Keycloak AIA status=${aia.status ?? 'missing'} (action=${aia.action}).`
          : ''
      const account = accountSecurityUrl()
      toast.show({
        tone: 'warn',
        message: `${title} was not saved in Keycloak`,
        detail:
          canonicalId === 'passkey'
            ? `Account API has no webauthn credential on this user (UAT lists password+OTP types only — webauthn type is missing).${aiaBit} Try again on Keycloak Account${account ? ` (${account})` : ''}, or ask IdP ops to enable WebAuthn Register + verify user Credentials shows webauthn.`
            : 'Keycloak did not return this factor. Try setup again from Security.',
      })
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enrolled, qc])

  useEffect(() => {
    if (!removed) return
    if (toastedRemove.current === removed) return
    toastedRemove.current = removed
    setDeletingId(null)
    setRemoveIntent(null)
    const verifyOnly = removed === 'verify'
    // Drop enroll placeholders before refetch so stale local-* cannot keep Activated.
    for (const id of ['totp', 'passkey', 'recovery']) clearLocalMethodActive(id)
    search.delete('removed')
    setSearch(search, { replace: true })
    void (async () => {
      await qc.invalidateQueries({ queryKey: keys.mfaStatus })
      const fresh = await statusQuery.refetch()
      const data = fresh?.data
      const stillOn = (data?.activeMethodIds ?? []).some((id) => isRemovableMethod(id))
      if (verifyOnly) {
        if (!data?.fromAccountApi) {
          toast.show({
            tone: 'warn',
            message: 'Could not verify removal',
            detail: 'Refresh status. If Authenticator is still Activated, delete failed in Keycloak.',
          })
          return
        }
        if (stillOn) {
          toast.mfaRemoveError(
            'Keycloak still has this factor. Enable the delete_credential required action on the realm, or remove it in Keycloak Account console.',
          )
          return
        }
      }
      if (removed === 'all') toast.mfaRemovedAll()
      else toast.mfaRemoved()
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [removed, qc])

  // Delete → Keycloak can leave this page in bfcache with deletingId stuck (busy forever).
  useEffect(() => {
    const clearBusy = () => setDeletingId(null)
    clearBusy()
    const onPageShow = () => clearBusy()
    const onVisible = () => {
      if (document.visibilityState === 'visible') clearBusy()
    }
    window.addEventListener('pageshow', onPageShow)
    window.addEventListener('focus', clearBusy)
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      window.removeEventListener('pageshow', onPageShow)
      window.removeEventListener('focus', clearBusy)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [])

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') void statusQuery.refetch()
    }
    document.addEventListener('visibilitychange', onVisible)
    window.addEventListener('focus', onVisible)
    return () => {
      document.removeEventListener('visibilitychange', onVisible)
      window.removeEventListener('focus', onVisible)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const activeIds = useMemo(
    () =>
      new Set([
        ...(status?.activeMethodIds ?? []),
        ...(enrolled ? [canonicalMfaMethodId(enrolled)] : []),
      ]),
    [status?.activeMethodIds, enrolled],
  )
  const activeCreds = (status?.credentials ?? []).filter((c) => {
    const t = c.type.toLowerCase()
    return t !== 'password' && t !== 'password-history'
  })
  const anyMfa = activeIds.size > 0
  const statusUnknown =
    Boolean(status?.error) && !status?.fromAccountApi && !demo && !statusQuery.isLoading
  const activeFactorIds = [...activeIds].filter((id) => isRemovableMethod(id))
  const primaryOn = activePrimaryMfaId(activeIds)
  const bothPrimariesOn = activeIds.has('totp') && activeIds.has('passkey')

  const readyMethods = MFA_HUB_METHODS.filter((m) => m.status === 'ready')
  const laterMethods = MFA_HUB_METHODS.filter((m) => m.status !== 'ready')
  const allMethods = [...readyMethods, ...laterMethods]
  const passkeyActive = activeIds.has('passkey')

  const clearStuckForIntent = () => {
    if (!removeIntent) return
    if (removeIntent.kind === 'all') {
      for (const id of ['totp', 'passkey', 'recovery']) clearLocalMethodActive(id)
    } else {
      clearLocalMethodActive(removeIntent.methodId)
    }
    setRemoveIntent(null)
    void qc.invalidateQueries({ queryKey: keys.mfaStatus })
    toast.mfaRemoved()
  }

  const credentialsForIntent = useMemo(() => {
    if (!removeIntent) return []
    if (removeIntent.kind === 'all') return activeCreds.filter((c) => isDeletableCredentialId(c.id))
    return credsForMethod(removeIntent.methodId, activeCreds).filter((c) =>
      isDeletableCredentialId(c.id),
    )
  }, [removeIntent, activeCreds])

  // Stale local-* placeholders leave the Remove modal empty — pull real Keycloak ids.
  useEffect(() => {
    if (!removeIntent) return
    if (credentialsForIntent.length > 0) return
    void statusQuery.refetch()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [removeIntent])

  const deleteCredential = async (cred: MfaCredentialInfo) => {
    if (demo) {
      toast.show({
        tone: 'info',
        message: 'Sign in with Keycloak to remove devices',
        detail: 'Demo sessions cannot manage MFA devices.',
      })
      return
    }
    setDeletingId(cred.id)
    try {
      const result = await removeMfaCredential(cred.id, '/settings/security?removed=1')
      if (result.mode === 'direct') {
        forgetCredential(cred.id)
        for (const mid of methodIdsForCredentialType(cred.type)) clearLocalMethodActive(mid)
        setDeletingId(null)
        setRemoveIntent(null)
        void qc.invalidateQueries({ queryKey: keys.mfaStatus })
        search.set('removed', '1')
        setSearch(search, { replace: true })
        // Toast comes from ?removed= effect (avoids double success).
        return
      }
      // AIA redirects away — do not clear local Active until Keycloak confirms
      // (?removed=1). Optimistic clear made cancelled deletes look successful.
    } catch (err) {
      setDeletingId(null)
      toast.mfaRemoveError(
        err instanceof Error ? err.message : 'Try Refresh status, then remove again.',
      )
    }
  }

  const copy = removeIntent ? removeCopy(removeIntent) : null

  const startChangePassword = () => {
    if (demo) {
      toast.show(toastCopy.passwordNeedKeycloak)
      return
    }
    setPwdBusy(true)
    void beginChangePassword().catch((err) => {
      setPwdBusy(false)
      toast.show(
        toastCopy.passwordChangeError(err instanceof Error ? err.message : undefined),
      )
    })
  }

  return (
    <div className="w7-mfa-page">
      <PageHead
        title="Two-factor authentication"
        subtitle="Optional vault for your agent sign-in — not downline adoption. Add a method, manage devices, or turn protection off. See who beneath you has 2FA under People → Sign-in 2FA."
      />

      <section className="w7-pwd-strip" aria-labelledby="w7-pwd-strip-title">
        <div className="w7-pwd-strip-copy">
          <p className="w7-pwd-strip-kicker">
            <Icon name="lock" size={14} />
            Sign-in password
          </p>
          <h2 id="w7-pwd-strip-title" className="w7-pwd-strip-title">
            Change password
          </h2>
          <p className="w7-pwd-strip-body">
            Opens Keycloak to set a new password, then returns here. Demo sessions cannot change
            it.
          </p>
        </div>
        <Button
          variant="secondary"
          icon="lock"
          busy={pwdBusy}
          disabled={demo}
          onClick={startChangePassword}
        >
          Change password
        </Button>
      </section>

      {demo ? (
        <div style={{ marginBottom: 'var(--w7-s4)' }}>
          <StateBlock
            icon="shield"
            title="Demo session cannot enroll MFA"
            actions={
              <Button variant="primary" onClick={() => navigate('/login')}>
                Sign in with Keycloak
              </Button>
            }
          >
            Sign in with a Keycloak user to set up or turn off authenticator apps and passkeys.
          </StateBlock>
        </div>
      ) : (
        <section
          className="w7-mfa-hero"
          data-state={anyMfa ? 'on' : 'off'}
          aria-live="polite"
        >
          <Button
            className="w7-mfa-hero-refresh"
            variant="ghost"
            size="sm"
            icon="refresh"
            busy={statusQuery.isFetching}
            onClick={() => void statusQuery.refetch()}
          >
            Refresh status
          </Button>

          <div className="w7-mfa-hero-visual" aria-hidden>
            <img
              src={anyMfa ? '/auth/mfa/mfa-hero-protected.png' : '/auth/mfa/mfa-hero-open.png'}
              alt=""
              loading="eager"
              decoding="async"
            />
            <div className="w7-mfa-hero-visual-scrim" />
          </div>

          <div className="w7-mfa-hero-main">
            <div className="w7-mfa-hero-body">
              <p className="w7-mfa-hero-kicker">
                <Icon name={statusUnknown ? 'warning' : anyMfa ? 'shield' : 'lock'} size={14} />
                {statusQuery.isLoading
                  ? 'Checking…'
                  : statusUnknown
                    ? '2FA status unknown'
                    : anyMfa
                      ? '2FA is on'
                      : '2FA is off'}
              </p>
              <h2 className="w7-mfa-hero-title">
                {statusQuery.isLoading
                  ? 'Checking your second factors…'
                  : statusUnknown
                    ? 'Could not read your factors from Keycloak'
                    : anyMfa
                      ? 'Your account is protected'
                      : 'Add a second factor when you’re ready'}
              </h2>
              <p className="w7-mfa-hero-body-copy">
                {statusUnknown
                  ? 'Refresh status. If this stays unknown, sign out/in again — the hub was treating a failed status fetch as Off.'
                  : anyMfa
                    ? 'Sign-in can ask for a method below after your password. Only one of Authenticator or Passkey can be on — turn the other off first. Deletes confirm in Keycloak; this console never stores secrets.'
                    : 'Pick authenticator app or passkey (not both). Email and SMS are not live yet.'}
              </p>
              {bothPrimariesOn ? (
                <p className="w7-mfa-hero-body-copy" data-testid="mfa-both-primary-warn" style={{ marginTop: 8 }}>
                  Both Authenticator and Passkey are on. Login only challenges the authenticator —
                  remove it below if you want Passkey at sign-in, or remove Passkey to keep the app.
                </p>
              ) : null}
              {anyMfa && activeFactorIds.length > 0 ? (
                <div className="w7-mfa-hero-factors">
                  {activeFactorIds.map((id) => (
                    <span key={id} className="w7-mfa-factor-badge">
                      <img
                        src={
                          id === 'totp'
                            ? '/auth/mfa/mfa-totp.png'
                            : id === 'recovery'
                              ? '/auth/mfa/mfa-recovery.png'
                              : '/auth/mfa/mfa-passkey.png'
                        }
                        alt=""
                        aria-hidden
                      />
                      {shortFactorLabel(id)}
                    </span>
                  ))}
                </div>
              ) : null}
            </div>

            <aside className="w7-mfa-hero-aside">
              <p className="w7-mfa-hero-aside-label">
                {anyMfa ? 'Manage protection' : 'Suggested start'}
              </p>
              <div className="w7-mfa-manage">
                {anyMfa ? (
                  <button
                    type="button"
                    className="w7-mfa-manage-btn"
                    data-tone="danger"
                    onClick={() => setRemoveIntent({ kind: 'all' })}
                  >
                    <span className="w7-mfa-manage-btn-media" aria-hidden>
                      <img src="/auth/mfa/mfa-action-turnoff.png" alt="" />
                    </span>
                    <span className="w7-mfa-manage-btn-copy">
                      <strong>Turn off 2FA</strong>
                      <span>Remove every factor after Keycloak confirms</span>
                    </span>
                  </button>
                ) : (
                  <button
                    type="button"
                    className="w7-mfa-manage-btn"
                    data-tone="primary"
                    onClick={() => navigate('/settings/security/totp')}
                  >
                    <span className="w7-mfa-manage-btn-media" aria-hidden>
                      <img src="/auth/mfa/mfa-totp.png" alt="" />
                    </span>
                    <span className="w7-mfa-manage-btn-copy">
                      <strong>Set up authenticator</strong>
                      <span>Fastest start — 6-digit app codes</span>
                    </span>
                  </button>
                )}
              </div>
            </aside>
          </div>
        </section>
      )}

      <p className="w7-mfa-note">
        <span className="w7-mfa-note-label">Tip · </span>
        Authenticator and Passkey are alternatives — keep only one on. One Passkey covers Mac Touch
        ID, Android fingerprint, Face ID, Windows Hello, or a USB key.
      </p>

      <div className="w7-mfa-grid">
        {allMethods.map((m) => {
          const active = activeIds.has(m.id)
          const coveredByPasskey = isWebAuthnShortcut(m.id) && passkeyActive && !active
          const rivalId = isPrimaryMfaMethod(m.id) ? rivalPrimaryMfaId(m.id) : null
          const blockedByRival =
            !active && rivalId && primaryOn === rivalId
              ? shortFactorLabel(rivalId)
              : undefined
          const canRemove = active && isRemovableMethod(m.id) && !demo
          return (
            <MethodTile
              key={m.id}
              method={m}
              active={active}
              coveredByPasskey={coveredByPasskey}
              blockedByRival={blockedByRival}
              demo={demo}
              onSetup={() => {
                if (!m.kcAction || blockedByRival) return
                navigate(`/settings/security/${m.id}`)
              }}
              onRemove={
                canRemove
                  ? () =>
                      setRemoveIntent({
                        kind: 'method',
                        methodId: m.id as 'totp' | 'passkey' | 'recovery',
                      })
                  : undefined
              }
            />
          )
        })}
      </div>

      <Modal
        open={removeIntent !== null}
        onClose={() => {
          if (!deletingId) setRemoveIntent(null)
        }}
        title={copy?.title ?? 'Remove second factor'}
        subtitle={copy?.subtitle}
        closeDisabled={Boolean(deletingId)}
        footer={
          <Button variant="ghost" disabled={Boolean(deletingId)} onClick={() => setRemoveIntent(null)}>
            Cancel
          </Button>
        }
      >
        {credentialsForIntent.length > 0 ? (
          <ul className="w7-mfa-device-list" data-testid="mfa-delete-list">
            {credentialsForIntent.map((cred) => (
              <li key={cred.id} className="w7-mfa-device-row">
                <div className="w7-mfa-device-meta">
                  <strong data-testid={`mfa-device-name-${cred.id}`}>{deviceDisplayName(cred)}</strong>
                  <span className="w7-dim">{deviceDisplayMeta(cred)}</span>
                </div>
                <Button
                  variant="danger"
                  icon="close"
                  busy={deletingId === cred.id}
                  disabled={Boolean(deletingId)}
                  onClick={() => void deleteCredential(cred)}
                  data-testid={`mfa-delete-${cred.id}`}
                  aria-label={`Remove ${deviceDisplayName(cred)}`}
                >
                  {deletingId === cred.id ? 'Removing…' : 'Remove this device'}
                </Button>
              </li>
            ))}
          </ul>
        ) : (
          <div>
            <p className="w7-mfa-step-body" style={{ marginTop: 0 }}>
              No Keycloak credential id for this factor yet — either it was already removed, or
              status is stale. Refresh, then try Delete again. Or turn it off here to clear the
              Activated state so you can switch methods.
            </p>
            <div className="w7-row" style={{ gap: 'var(--w7-s3)', marginTop: 'var(--w7-s4)', flexWrap: 'wrap' }}>
              <Button
                variant="danger"
                icon="close"
                data-testid="mfa-turn-off-stuck"
                onClick={clearStuckForIntent}
              >
                Turn off
              </Button>
              <Button
                variant="secondary"
                icon="refresh"
                busy={statusQuery.isFetching}
                onClick={() => void statusQuery.refetch()}
              >
                Refresh status
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  )
}
